import React, { useState, forwardRef, useCallback } from 'react'
import dayjs from 'dayjs'
import { useParams } from 'react-router-dom'
import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/dist/styles.css'

import { useOrderedGetGalleryQuery, GalleryMediaItem } from 'shared/extended'
import PageHeader from 'src/components/PageHeader'
import { GalleryPhotoCard, GalleryPhotoImage, GalleryButtonsContainer, Edit, Delete } from '../styled'
import { useRemoveGalleryPhotoMutation, GetGalleryQuery, useUpdateGalleryPhotoOrderMutation } from 'shared/schema'
import { useUserRoles } from 'src/components/Roles'
import { SortableGrid } from '../sortable-grid'
import VideoLightbox from '../VideoLightbox'
type PhotoCardProps = { onClick: any; data: GetGalleryQuery['gallery']['photos'][number]; style?: any }
const PhotoCard = forwardRef<HTMLDivElement, PhotoCardProps>(
	({ data, onClick, style, ...props }: PhotoCardProps, ref) => {
		const { isGalleryAdmin: canEdit } = useUserRoles()
		const [deletePhoto] = useRemoveGalleryPhotoMutation({
			refetchQueries: ['getGallery'],
			awaitRefetchQueries: true
		})

		const inlineStyles = {
			height: 200,
			position: 'relative',
			...style
		}

		const thumbnailUrl = data.image.publicUrl.replace('/original_', '/thumb_')
		return (
			<div ref={ref} style={inlineStyles} {...props}>
				<GalleryPhotoCard onClick={onClick}>
					<GalleryPhotoImage
						style={{
							backgroundImage: `url(${thumbnailUrl})`
						}}
					/>

					{canEdit && (
						<GalleryButtonsContainer>
							<Edit onClick={() => console.log('edit')} />
							<Delete
								onClick={(ev) => {
									ev.stopPropagation()
									if (confirm('Are you sure you want to delete this photo?')) {
										deletePhoto({
											variables: {
												id: data.id
											}
										})
									}
								}}
							/>
						</GalleryButtonsContainer>
					)}
				</GalleryPhotoCard>
			</div>
		)
	}
)

function GalleryView() {
	const { galleryId } = useParams()
	const { data, media } = useOrderedGetGalleryQuery(+galleryId)
	const [lightboxOpen, setLightboxOpen] = useState(false)
	const [mediaIndex, setMediaIndex] = useState(0)

	const openLightbox = useCallback((idx: number) => {
		setMediaIndex(idx)
		setLightboxOpen(true)
	}, [])

	if (!data?.gallery?.title) {
		return null
	}
	const pageTitle = `${data.gallery.title} (${dayjs(data.gallery.date?.split('T')[0]).format('ll')})`
	const hasVideo = media.length > 0 && media[0].type === 'video'

	// Photo-only offset: when video exists, photo grid click index maps to media index + 1
	const photoMediaOffset = hasVideo ? 1 : 0

	const numMedia = media.length
	const currentMedia = media[mediaIndex]

	const prevIndex = (mediaIndex + numMedia - 1) % numMedia
	const nextIndex = (mediaIndex + 1) % numMedia

	// Photo-only slide list for the image lightbox (videos are handled by VideoLightbox)
	const photoSlides = media
		.map((item, idx) => ({ item, mediaIdx: idx }))
		.filter(({ item }) => item.type === 'photo')
		.map(({ item, mediaIdx }) => ({ src: (item as { url: string }).url, mediaIdx }))
	const photoSlideIndex = photoSlides.findIndex((s) => s.mediaIdx === mediaIndex)
	const numPhotos = photoSlides.length

	return (
		<>
			<PageHeader parents={[{ to: '/gallery', title: 'All Albums' }]} primaryColor={false}>
				{pageTitle}
			</PageHeader>

			{/* Video card above photo grid */}
			{hasVideo && media[0].type === 'video' && (
				<div style={{ marginBottom: '16px', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gridGap: 8, padding: 8 }}>
					<div
						onClick={() => openLightbox(0)}
						style={{ height: 200, position: 'relative', cursor: 'pointer', overflow: 'hidden', borderRadius: '4px' }}
					>
						<GalleryPhotoImage style={{ backgroundImage: `url(${media[0].thumbnailUrl})` }} />
						<div style={{
							position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
							display: 'flex', alignItems: 'center', justifyContent: 'center',
							backgroundColor: 'rgba(0,0,0,0.3)'
						}}>
							<div style={{
								width: 48, height: 48, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.85)',
								display: 'flex', alignItems: 'center', justifyContent: 'center'
							}}>
								<div style={{ width: 0, height: 0, borderTop: '12px solid transparent', borderBottom: '12px solid transparent', borderLeft: '20px solid #333', marginLeft: '4px' }} />
							</div>
						</div>
					</div>
				</div>
			)}

			<SortableGrid
				GridItemComponent={PhotoCard}
				reorderHook={useUpdateGalleryPhotoOrderMutation}
				reorderHookConfig={{
					refetchQueries: ['getGallery'],
					awaitRefetchQueries: true
				}}
				reorderMutationVariables={{
					id: +galleryId
				}}
				initialItems={data?.gallery?.photos}
				gridColumns={5}
				onGridItemClick={(index) => {
					openLightbox(index + photoMediaOffset)
				}}
			/>

			{/* Lightbox: video or image */}
			{lightboxOpen && currentMedia?.type === 'video' && (
				<VideoLightbox
					src={currentMedia.url}
					poster={currentMedia.thumbnailUrl}
					onClose={() => setLightboxOpen(false)}
					onPrev={numMedia > 1 ? () => setMediaIndex(prevIndex) : undefined}
					onNext={numMedia > 1 ? () => setMediaIndex(nextIndex) : undefined}
					caption={`Video - 1 of ${numMedia}`}
				/>
			)}
			{lightboxOpen && currentMedia?.type === 'photo' && (
				<Lightbox
					open={lightboxOpen}
					close={() => setLightboxOpen(false)}
					index={photoSlideIndex}
					slides={photoSlides.map(({ src }, i) => ({
						src,
						description: `Image ${i + 1} of ${numPhotos}`
					}))}
					on={{
						view: ({ index }) => {
							const slide = photoSlides[index]
							if (slide) setMediaIndex(slide.mediaIdx)
						}
					}}
				/>
			)}
		</>
	)
}

export default GalleryView
